// src/pages/Enquire.jsx
import React, { useState } from "react";
import Container from "../components/Container.jsx";
import { textOnlyOptions, formatIntl } from "../lib/countries";
import { openCalendlyPopup } from "../lib/calendly";

/** Strip +CC / 00CC and any leading 0 the browser might inject (keeps digits only). */
function scrubLocal(raw = "", cc = "+44") {
  let v = String(raw).replace(/[\s-]+/g, "");
  const ccNum = String(cc || "+").replace("+", "");
  if (ccNum) {
    const re = new RegExp(`^(?:\\+|00)?${ccNum}\\s*0?`);
    v = v.replace(re, "");
  }
  return v.replace(/^\+/, "");
}

export default function Enquire() {
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Controlled phone bits (so autofill doesn’t put +44 into the local box)
  const [countryCode, setCountryCode] = useState("+44");
  const [phoneLocal, setPhoneLocal] = useState("");

  // Save the last submitted answers so Calendly can be prefilled after submit
  const [prefill, setPrefill] = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    const f = e.currentTarget;

    try {
      // Compose E.164 phone (same logic as other pages)
      const localClean = scrubLocal(f.phoneLocal.value, f.countryCode.value);
      const phoneE164 = formatIntl(f.countryCode.value, localClean);
      const cleanEmail = String(f.email.value || "").trim().toLowerCase();

      // Stash values for Calendly (these survive when the form unmounts)
      const snapshot = {
        name: f.name.value,
        email: cleanEmail,
        phoneIntl: phoneE164,
        readyToInvest: f.readyToInvest.value,
        amountToInvest: f.amountToInvest.value,
        consent: !!f.consent.checked,
      };
      setPrefill(snapshot);

      // Payload to Apps Script
      const data = {
        name: snapshot.name,
        email: snapshot.email,
        countryCode: f.countryCode.value,
        phone: snapshot.phoneIntl,
        message: f.message.value,
        readyToInvest: snapshot.readyToInvest,
        amountToInvest: snapshot.amountToInvest,
        consent: snapshot.consent ? "yes" : "no",
        timestamp: new Date().toISOString(),
        source: "web-enquiry",
      };

      const res = await fetch("/enquire", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded;charset=UTF-8" },
        body: new URLSearchParams(data),
      });

      const text = await res.text();
      let ok = res.ok, serverErr = "";
      try {
        const j = JSON.parse(text);
        if (typeof j?.ok === "boolean") ok = j.ok;
        if (j?.error) serverErr = j.error;
      } catch { /* non-JSON fallback */ }

      if (!ok) throw new Error(serverErr || "Submit failed");

      window.gtag?.("event", "generate_lead", { form_name: "enquiry" });

      // Swap to the thank-you view
      setSubmitted(true);

      // Reset the form UI (we’ve already saved what we need into `prefill`)
      f.reset();
      setPhoneLocal("");
      setCountryCode("+44");
    } catch (err) {
      console.error(err);
      setError(err?.message || "Submission error.");
    } finally {
      setLoading(false);
    }
  }

  function openCalendlyFromForm() {
    if (!prefill) return;
    openCalendlyPopup({
      name: prefill.name,
      email: prefill.email,
      a1: prefill.phoneIntl,         // Phone
      a2: prefill.readyToInvest,     // When planning to invest
      a3: prefill.amountToInvest,    // Amount to invest
      utm: { utmSource: "enquire", utmMedium: "complete", utmCampaign: "hero" },
    });
  }

  return (
    <main className="py-10 sm:py-16">
      <Container>
        <h1 className="text-2xl font-semibold text-[#003366]">Enquiry</h1>

        {submitted ? (
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-xl font-semibold text-[#003366]">Thanks — your enquiry is in</h2>
            <p className="mt-2 text-slate-700">
              We’ve received your details. One of the team will get in touch to understand your goals and share
              tailored options.
            </p>
            <ul className="mt-4 space-y-1 text-slate-700">
              <li>• What to expect: a quick call to clarify your brief and next steps.</li>
              <li>• Timing: usually within one business day.</li>
              <li>• If you’d like to speak sooner, you can book a call with us below.</li>
            </ul>
            <div className="mt-4 flex gap-3">
              <button
                type="button"
                onClick={openCalendlyFromForm}
                disabled={!prefill}
                className="inline-flex items-center px-4 rounded border border-[#003366] text-[#003366] disabled:opacity-50"
                aria-label="Book a call on Calendly"
              >
                Book a call
              </button>
              <a
                href="#/guides"
                className="rounded-xl border border-slate-300 px-4 py-2 text-[#003366] hover:bg-slate-50"
              >
                Explore our guides
              </a>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="mt-6 grid gap-4 max-w-2xl">
            <div>
              <label className="block text-sm font-medium">Name</label>
              <input
                name="name"
                type="text"
                required
                autoComplete="name"
                className="mt-1 w-full rounded border px-3 py-2"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Email</label>
              <input
                name="email"
                type="email"
                required
                autoComplete="email"
                className="mt-1 w-full rounded border px-3 py-2"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium">Phone</label>
              <div className="mt-1 flex gap-2">
                <select
                  name="countryCode"
                  required
                  value={countryCode}
                  onChange={(e) => setCountryCode(e.target.value)}
                  className="rounded border px-3 py-2"
                  disabled={loading}
                  autoComplete="tel-country-code"
                >
                  {textOnlyOptions().map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                <input
                  name="phoneLocal"
                  type="tel"
                  required
                  value={phoneLocal}
                  onChange={(e) => setPhoneLocal(scrubLocal(e.target.value, countryCode))}
                  className="flex-1 rounded border px-3 py-2"
                  disabled={loading}
                  autoComplete="tel-national"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm">When are you planning to make your next investment?</label>
              <select
                name="readyToInvest"
                required
                className="w-full rounded border px-3 py-2"
                defaultValue=""
                disabled={loading}
              >
                <option value="" disabled>Select an option…</option>
                <option>Ready to invest now.</option>
                <option>Within 3 months.</option>
                <option>3-6 months.</option>
                <option>Just exploring for now.</option>
              </select>
            </div>

            <div>
              <label className="block text-sm">What level of funds are you planning to allocate?</label>
              <select
                name="amountToInvest"
                required
                className="w-full rounded border px-3 py-2"
                defaultValue=""
                disabled={loading}
              >
                <option value="" disabled>Select an option…</option>
                <option>Under £100,000</option>
                <option>£100,000 – £250,000</option>
                <option>£250,000 - £500,000</option>
                <option>£500,000 plus</option>
                <option>Unknown</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium">Message</label>
              <textarea name="message" rows="4" className="mt-1 w-full rounded border px-3 py-2" disabled={loading} />
            </div>

            <label className="flex items-start gap-2 text-sm">
              <input name="consent" type="checkbox" className="mt-1" required disabled={loading} />
              <span>I agree to be contacted about related services and have read the privacy notice.</span>
            </label>

            {error && <div className="text-red-600 font-medium">{error}</div>}

            <button type="submit" disabled={loading} className="rounded bg-[#003366] text-white px-5 py-2">
              {loading ? "Sending..." : "Send enquiry"}
            </button>
          </form>
        )}
      </Container>
    </main>
  );
}
